import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { Tooltip } from '@/components/ui/tooltip';
import { noyauPillInkClass } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import { type NoyauCode, noyauOf } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * The RETAINED noyau of a line of Sessions (noyau.md §9.2, revue du
 * 2026-09-25): a pill in the noyau's official colour, written by its TRIPLET
 * to hold in the line, its name in the bubble.
 *
 * ## A link to the session, and not a mute span
 *
 * « La ligne ENTIÈRE ouvre la session » (`sessions-table.tsx`): the row's link
 * lays a veil over the whole line, and a pill under it would never see the
 * pointer that opens its bubble. So the pill stands ABOVE the veil
 * (`relative z-10`) and is itself the same link - a click on it still opens the
 * session. It leaves the tab order and the accessibility tree to the row's own
 * link, whose line carries the name in words (`sr-only`), so a keyboard and a
 * screen reader meet the session once and the noyau by its name.
 *
 * ## The trial's pill (#170)
 *
 * On a line that retained no noyau, the column shows the principal of the last
 * trial run from the session (§9.4): the SAME pill, filled in the noyau's
 * colour (revue du 2026-09-29), told apart only by « lu à blanc » in its bubble
 * and for a screen reader - a reading of the instrument, never a retained noyau.
 *
 * The colour is the table's, in a style and never a hex written here - the
 * régime of `reglages/noyau-card.tsx` - and the ink is the one the WCAG
 * arithmetic picks between the two tokens (`NOYAU_PILL_INK`).
 */

export interface NoyauPillProps {
  readonly code: NoyauCode;
  readonly sessionId: string;
  /** The principal of a trial rather than the retained noyau: worded so, drawn alike. */
  readonly trial?: boolean;
}

export function NoyauPill({ code, sessionId, trial = false }: NoyauPillProps) {
  const noyau = noyauOf(code);
  return (
    <>
      <Tooltip label={trial ? `${noyau.name} (lu à blanc)` : noyau.name} describes={false}>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId }}
          tabIndex={-1}
          aria-hidden="true"
          data-testid={SESSIONS_TEST_IDS.rowNoyau}
          data-noyau={code}
          data-source={trial ? 'trial' : undefined}
          className={cn(
            'relative z-10 inline-flex h-5 min-w-8 items-center justify-center rounded-pill px-2 font-bold text-micro leading-none tracking-wide',
            noyauPillInkClass(code),
          )}
          style={{ backgroundColor: noyau.color }}
        >
          {code}
        </Link>
      </Tooltip>
      <span className="sr-only">
        {trial ? `Noyau lu à blanc : ${noyau.name}` : `Noyau retenu : ${noyau.name}`}
      </span>
    </>
  );
}
